import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Box from '@material-ui/core/Box';
import Collapse from '@material-ui/core/Collapse';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import LocalShippingIcon from '@material-ui/icons/LocalShipping';
import { invoiceFromOrders } from '../modules/invoicedata';
import { getProductionOrderDataFull, getPoForOrder, updateOrderStatus } from '../modules/orderdata';
import Order, { getOrderDisplayNumber } from '../domain/order';
import ConfirmationDialog from '../common/confirmationdialog';
import moment from 'moment';
import Spinner from '../common/spinner';
import Chip from '@material-ui/core/Chip';
import AddIcon from '@material-ui/icons/Add';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import ReceiptIcon from '@material-ui/icons/Receipt';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Radio from '@material-ui/core/Radio';
import TextField from '@material-ui/core/TextField';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import FilterListIcon from '@material-ui/icons/FilterList';
import TablePagination from '@material-ui/core/TablePagination';

const useStyles = makeStyles((theme) => ({
    root: {
        '& > *': {
            borderBottom: 'unset',
        },
    },
    buttonRow: {
        float: 'right',
    },
    receiptIcon: {
        verticalAlign: 'middle',
        display: 'inline-flex',
    },
    filterPanel: {
        marginBottom: theme.spacing(2),
    },
    filterAccordion: {
        backgroundColor: theme.palette.background.default,
    },
    filterField: {
        minWidth: 200,
        marginRight: theme.spacing(2),
        marginBottom: theme.spacing(1),
    },
    filterActions: {
        display: 'flex',
        gap: theme.spacing(1),
        marginTop: theme.spacing(1),
    },
}));

const columns = [
    { id: 'ordernumbero', label: 'general.orderNumber' },
    { id: 'orderdate', label: 'general.ordered' },
    { id: 'customerName', label: 'production.customerAndLocation' },
    { id: 'orderer', label: 'general.orderer' },
    //{ id: 'portfolio', label: 'Salkku' },
];

// Memoized OrderRow component to prevent unnecessary re-renders
const OrderRow = React.memo(function OrderRow(props) {
    const order = props.row;
    const [open, setOpen] = React.useState(false);
    const [confirmMakeInvoiceOpen, setConfirmMakeInvoiceOpen] = React.useState(false);
    const [markAsDeliveredDialogOpen, setMarkAsDeliveredDialogOpen] = React.useState(false);
    const [deliveryStatus, setDeliveryStatus] = React.useState('delivered');
    const [deliveryReason, setDeliveryReason] = React.useState('');
    const [poBlobUrl, setPoBlobUrl] = React.useState('');
    const [disabled, setDisabled] = React.useState(false);

    // Use a selector that only selects the specific order to prevent re-renders
    const orderFull = useSelector(
        (state) => state.orderdata.productionOrderDataByOrderNumber[order.orderNumber],
        shallowEqual
    );

    const classes = useStyles();
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const makeInvoice = () => {
        setConfirmMakeInvoiceOpen(true);
    };

    const onConfirmMakeInvoice = async () => {
        setDisabled(true);
        setConfirmMakeInvoiceOpen(false);
        if (!(await invoiceFromOrders([order.orderNumber])(dispatch))) {
            toast.error(t('production.invoiceGenerationFailed'), { autoClose: 5000, hideProgressBar: false });
            setDisabled(false);
            return;
        }
        toast.info(t('production.invoiceGenerated'), { autoClose: 2000, hideProgressBar: true });
        setOpen(false);
        setDisabled(false);
    };

    const onCancelConfirmInvoice = () => {
        setConfirmMakeInvoiceOpen(false);
    };

    const openMarkAsDeliveredDialog = () => {
        setMarkAsDeliveredDialogOpen(true);
        setDeliveryStatus(order.status === Order.StatusDelivered() ? 'deliveredNonInvoicable' : 'delivered');
        setDeliveryReason('');
    };

    const onConfirmMarkAsDelivered = async () => {
        setDisabled(true);
        setMarkAsDeliveredDialogOpen(false);

        const newStatus =
            deliveryStatus === 'delivered' ? Order.StatusDelivered() : Order.StatusDeliveredNonInvoicable();

        try {
            await updateOrderStatus(newStatus, order.orderNumber, deliveryReason)(dispatch);
            toast.success(t('production.orderMarkedAsDelivered'), { autoClose: 2000, hideProgressBar: true });
        } catch (error) {
            toast.error(t('production.markAsDeliveredFailed'), { autoClose: 5000, hideProgressBar: false });
        }

        setOpen(false);
        setDisabled(false);
    };

    const onCancelMarkAsDelivered = () => {
        setMarkAsDeliveredDialogOpen(false);
        setDeliveryReason('');
    };

    const expandOrder = async () => {
        if (!orderFull) {
            getProductionOrderDataFull(order.orderNumber)(dispatch);
        }
        setOpen(!open);

        var xlsxdata = await getPoForOrder(order.orderNumber);
        const buff = Buffer.from(xlsxdata, 'base64');
        const blob = new Blob([buff.buffer], {
            type: 'application/vnd.openxmlformats',
        });
        const url = URL.createObjectURL(blob);
        setPoBlobUrl(url);
    };

    return (
        <React.Fragment key={order.orderNumber}>
            <TableRow className={classes.root} onClick={() => expandOrder()}>
                <TableCell>
                    {getOrderDisplayNumber(order)}
                    &nbsp;
                    <Chip size="small" color={'primary'} label={order.orderStatusText()} />
                    {order.invoiceNumbers.length > 0 && <ReceiptIcon className={classes.receiptIcon} name="invoiced" />}
                </TableCell>
                <TableCell>{moment(order.orderSent).format('DD.MM.YYYY HH:mm')}</TableCell>
                <TableCell>
                    {order.customer.name} /
                    {order.orderItems.map((item, ind) => {
                        return ind < 2 ? (
                            <span key={item.id}>
                                {item.location ? item.location.name : item.deliveryRecipient}&nbsp;
                            </span>
                        ) : (
                            '.'
                        );
                    })}
                </TableCell>
                <TableCell>{order.ordererName}</TableCell>
                {/*<TableCell>N/A</TableCell>*/}
                <TableCell>
                    <IconButton aria-label="expand row" size="small">
                        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        {!orderFull && <Spinner />}
                        {orderFull && (
                            <Box margin={1}>
                                <Grid item xs={12} className={classes.buttonRow}>
                                    {poBlobUrl.length > 0 && (
                                        <Button
                                            href={poBlobUrl}
                                            variant="contained"
                                            color="primary"
                                            disabled={disabled}
                                            download={`ostotilaus_${getOrderDisplayNumber(order)}.xlsx`}>
                                            {t('production.purchaseOrder')}
                                        </Button>
                                    )}
                                    {order.invoiceNumbers.length > 0 && (
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            disabled={disabled}
                                            name="btn-open-invoices"
                                            onClick={() => {
                                                props.history.push(`/invoices?order=${order.orderNumber}`);
                                            }}>
                                            <i className="fas fa-file-invoice-dollar"></i>
                                            &nbsp;{t('production.openInvoice')}
                                        </Button>
                                    )}
                                    &nbsp;
                                    {order.status !== Order.StatusDeliveredNonInvoicable() && (
                                        <React.Fragment>
                                            <Button
                                                variant="contained"
                                                color="secondary"
                                                name="btn-mark-delivered"
                                                onClick={openMarkAsDeliveredDialog}
                                                disabled={disabled}
                                                startIcon={<LocalShippingIcon />}>
                                                {t('production.delivered')}
                                            </Button>
                                            &nbsp;
                                        </React.Fragment>
                                    )}
                                    {order.status !== Order.StatusDeliveredNonInvoicable() && (
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            name="btn-make-invoice"
                                            onClick={makeInvoice}
                                            startIcon={<AddIcon />}>
                                            {t('production.makeInvoice')}
                                        </Button>
                                    )}
                                </Grid>
                                <Grid item container xs={12}>
                                    <Grid item xs={12} sm={6}>
                                        <strong>{t('general.state')}</strong>
                                        <br />
                                        {orderFull.orderStatusText()}
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <strong>{t('general.deliveryDate')}</strong>
                                        <br />
                                        {moment(orderFull.deliveryTime).isValid() && (
                                            <span>{moment(orderFull.deliveryTime).format('DD.MM.YYYY')}</span>
                                        )}
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <strong>{t('parameterCategories.DeliveryTerms')}</strong>
                                        <br />
                                        {orderFull.deliveryTerms}
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <strong>{t('parameterCategories.DeliveryMethod')}</strong>
                                        <br />
                                        {orderFull.deliveryMethod}
                                    </Grid>
                                    {orderFull.deliveryInfo && (
                                        <Grid item xs={12}>
                                            <strong>{t('production.delivered')}</strong>
                                            <br />
                                            {moment(orderFull.deliveryInfo.actualDeliveryDate).format('DD.MM.YYYY')}
                                            <br />
                                            {t('production.boxes')} {orderFull.deliveryInfo.boxCount}
                                            <br />
                                            {t('production.pallets')} {orderFull.deliveryInfo.palletCount}
                                        </Grid>
                                    )}
                                </Grid>
                                <Typography variant="h6" gutterBottom component="div">
                                    {t('order.orderedProducts')}
                                </Typography>
                                <Table size="small" aria-label="purchases">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>{t('general.location')}</TableCell>
                                            <TableCell>{t('general.product')}</TableCell>
                                            <TableCell>{t('pcs')}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {orderFull.orderItems.map((orderitem) => (
                                            <React.Fragment key={orderitem.id}>
                                                {orderitem.orderedFilters.map((ordfilt) => (
                                                    <TableRow key={ordfilt.id}>
                                                        <TableCell component="th" scope="row">
                                                            {orderitem.location
                                                                ? orderitem.location.name
                                                                : orderitem.deliveryRecipient}
                                                        </TableCell>
                                                        <TableCell>
                                                            {ordfilt.product
                                                                ? ordfilt.product.productName
                                                                : ordfilt.productBundle.name}
                                                        </TableCell>
                                                        <TableCell>{ordfilt.count}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </React.Fragment>
                                        ))}
                                    </TableBody>
                                </Table>
                            </Box>
                        )}
                    </Collapse>
                </TableCell>
            </TableRow>
            <ConfirmationDialog
                open={confirmMakeInvoiceOpen}
                confirm={onConfirmMakeInvoice}
                cancel={onCancelConfirmInvoice}
                confirmText={t('buttons.save')}>
                {' '}
                {t('production.confirmMakeNewInvoice')}
            </ConfirmationDialog>
            <Dialog
                open={markAsDeliveredDialogOpen}
                onClose={onCancelMarkAsDelivered}
                aria-labelledby="mark-delivered-dialog-title"
                maxWidth="sm"
                fullWidth>
                <DialogTitle id="mark-delivered-dialog-title">{t('production.markAsDelivered')}</DialogTitle>
                <DialogContent>
                    <RadioGroup value={deliveryStatus} onChange={(e) => setDeliveryStatus(e.target.value)}>
                        {order.status !== Order.StatusDelivered() && (
                            <FormControlLabel
                                value="delivered"
                                control={<Radio color="primary" />}
                                label={t('general.delivered')}
                            />
                        )}
                        <FormControlLabel
                            value="deliveredNonInvoicable"
                            control={<Radio color="primary" />}
                            label={t('general.deliveredNonInvoicable')}
                        />
                    </RadioGroup>
                    {deliveryStatus === 'deliveredNonInvoicable' && (
                        <TextField
                            autoFocus
                            margin="dense"
                            label={t('general.reason')}
                            type="text"
                            fullWidth
                            value={deliveryReason}
                            onChange={(e) => setDeliveryReason(e.target.value)}
                            required
                            error={deliveryStatus === 'deliveredNonInvoicable' && !deliveryReason.trim()}
                            style={{ marginTop: 16 }}
                        />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={onCancelMarkAsDelivered} variant="contained" color="secondary" disabled={disabled}>
                        {t('buttons.cancel')}
                    </Button>
                    <Button
                        onClick={onConfirmMarkAsDelivered}
                        color="primary"
                        disabled={disabled || (deliveryStatus === 'deliveredNonInvoicable' && !deliveryReason.trim())}
                        variant="contained">
                        {t('buttons.save')}
                    </Button>
                </DialogActions>
            </Dialog>
        </React.Fragment>
    );
});

export default function ProductionOrderListView(props) {
    const { t } = useTranslation();
    const classes = useStyles();
    const deliveryMethods = props.parameters ? props.parameters['DeliveryMethod'] : [];

    // Filter states - immediate states for input fields
    const [orderNumberFilterInput, setOrderNumberFilterInput] = useState('');
    const [customerNameFilterInput, setCustomerNameFilterInput] = useState('');
    const [ordererNameFilterInput, setOrdererNameFilterInput] = useState('');

    // Debounced filter states - used for actual filtering
    const [orderNumberFilter, setOrderNumberFilter] = useState('');
    const [customerNameFilter, setCustomerNameFilter] = useState('');
    const [ordererNameFilter, setOrdererNameFilter] = useState('');

    // Non-text filters don't need debouncing
    const [statusFilter, setStatusFilter] = useState([]);
    const [dateFromFilter, setDateFromFilter] = useState('');
    const [dateToFilter, setDateToFilter] = useState('');
    const [filterExpanded, setFilterExpanded] = useState(false);
    const [deliveryMethodFilter, setDeliveryMethodFilter] = useState('');

    // Pagination states
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(50);

    // Debounce effect for text inputs (300ms delay)
    useEffect(() => {
        const timer = setTimeout(() => {
            setOrderNumberFilter(orderNumberFilterInput);
        }, 300);
        return () => clearTimeout(timer);
    }, [orderNumberFilterInput]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setCustomerNameFilter(customerNameFilterInput);
        }, 300);
        return () => clearTimeout(timer);
    }, [customerNameFilterInput]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setOrdererNameFilter(ordererNameFilterInput);
        }, 300);
        return () => clearTimeout(timer);
    }, [ordererNameFilterInput]);

    // Reset to first page whenever filters change
    useEffect(() => {
        setPage(0);
    }, [
        orderNumberFilter,
        customerNameFilter,
        statusFilter,
        dateFromFilter,
        dateToFilter,
        ordererNameFilter,
        deliveryMethodFilter,
    ]);

    // Memoize filter logic to prevent recalculation on every render
    const filteredOrders = useMemo(() => {
        if (!props.productionOrders) return [];

        // Pre-parse date filters once
        const dateFrom = dateFromFilter ? moment(dateFromFilter).startOf('day') : null;
        const dateTo = dateToFilter ? moment(dateToFilter).endOf('day') : null;
        const statusFilterInt = statusFilter.length ? statusFilter.map((s) => parseInt(s)) : [];

        // Pre-lowercase filter strings
        const customerNameLower = customerNameFilter.toLowerCase();
        const ordererNameLower = ordererNameFilter.toLowerCase();

        return props.productionOrders.filter((order) => {
            // Order number filter - early return for quick filter
            if (orderNumberFilter && !order.orderNumber.toString().includes(orderNumberFilter)) {
                return false;
            }

            // Customer name filter
            if (customerNameFilter && !order.customer.name.toLowerCase().includes(customerNameLower)) {
                return false;
            }

            if (deliveryMethodFilter !== '' && order.deliveryMethod !== deliveryMethodFilter) {
                return false;
            }

            // Status filter - most selective, check early
            if (statusFilterInt.length && !statusFilterInt.includes(order.status)) {
                return false;
            }

            // Date filters - use pre-parsed dates
            if (dateFrom && moment(order.orderSent).isBefore(dateFrom)) {
                return false;
            }

            if (dateTo && moment(order.orderSent).isAfter(dateTo)) {
                return false;
            }

            // Orderer name filter
            if (ordererNameFilter && !order.ordererName.toLowerCase().includes(ordererNameLower)) {
                return false;
            }

            return true;
        });
    }, [
        props.productionOrders,
        orderNumberFilter,
        customerNameFilter,
        statusFilter,
        dateFromFilter,
        dateToFilter,
        ordererNameFilter,
        deliveryMethodFilter,
    ]);

    // Memoize clear filters handler
    const handleClearFilters = useCallback(() => {
        setOrderNumberFilterInput('');
        setCustomerNameFilterInput('');
        setOrdererNameFilterInput('');
        setOrderNumberFilter('');
        setCustomerNameFilter('');
        setOrdererNameFilter('');
        setStatusFilter([]);
        setDateFromFilter('');
        setDateToFilter('');
        setDeliveryMethodFilter('');
        setPage(0); // Reset to first page when clearing filters
    }, []);

    // Pagination handlers
    const handleChangePage = useCallback((event, newPage) => {
        setPage(newPage);
    }, []);

    const handleChangeRowsPerPage = useCallback((event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    }, []);

    // Memoize paginated orders
    const paginatedOrders = useMemo(() => {
        const start = page * rowsPerPage;
        return filteredOrders.slice(start, start + rowsPerPage);
    }, [filteredOrders, page, rowsPerPage]);

    // Memoize filter active check
    const hasActiveFilters = useMemo(
        () =>
            orderNumberFilter ||
            customerNameFilter ||
            statusFilter.length ||
            dateFromFilter ||
            dateToFilter ||
            deliveryMethodFilter ||
            ordererNameFilter,
        [
            orderNumberFilter,
            customerNameFilter,
            statusFilter,
            dateFromFilter,
            dateToFilter,
            ordererNameFilter,
            deliveryMethodFilter,
        ]
    );

    if (!props.productionOrders) return null;

    return (
        <div>
            {/* Filter Panel */}
            <Accordion
                className={classes.filterPanel}
                expanded={filterExpanded}
                onChange={() => setFilterExpanded(!filterExpanded)}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.filterAccordion}>
                    <Box display="flex" alignItems="center">
                        <FilterListIcon style={{ marginRight: 8 }} />
                        <Typography variant="h6">
                            {t('general.filters')}
                            {hasActiveFilters && (
                                <Chip
                                    size="small"
                                    label={filteredOrders.length}
                                    color="primary"
                                    style={{ marginLeft: 8 }}
                                />
                            )}
                        </Typography>
                    </Box>
                </AccordionSummary>
                <AccordionDetails>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={3}>
                            <TextField
                                className={classes.filterField}
                                label={t('general.orderNumber')}
                                value={orderNumberFilterInput}
                                onChange={(e) => setOrderNumberFilterInput(e.target.value)}
                                fullWidth
                                variant="outlined"
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <TextField
                                className={classes.filterField}
                                label={t('general.customer')}
                                value={customerNameFilterInput}
                                onChange={(e) => setCustomerNameFilterInput(e.target.value)}
                                fullWidth
                                variant="outlined"
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <TextField
                                className={classes.filterField}
                                label={t('general.orderer')}
                                value={ordererNameFilterInput}
                                onChange={(e) => setOrdererNameFilterInput(e.target.value)}
                                fullWidth
                                variant="outlined"
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <FormControl className={classes.filterField} fullWidth variant="outlined" size="small">
                                <InputLabel>{t('general.state')}</InputLabel>
                                <Select
                                    multiple
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                    label={t('general.state')}>
                                    <MenuItem value="4">{t('general.ordered')}</MenuItem>
                                    <MenuItem value="6">{t('general.orderPacked')}</MenuItem>
                                    <MenuItem value="7">{t('general.orderLoaded')}</MenuItem>
                                    <MenuItem value="8">{t('general.delivered')}</MenuItem>
                                    <MenuItem value="9">{t('general.deliveredNonInvoicable')}</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <FormControl className={classes.filterField} fullWidth variant="outlined" size="small">
                                <InputLabel>{t('parameterCategories.DeliveryMethod')}</InputLabel>
                                <Select
                                    value={deliveryMethodFilter}
                                    onChange={(e) => setDeliveryMethodFilter(e.target.value)}
                                    label={t('parameterCategories.DeliveryMethod')}>
                                    {deliveryMethods.map((method) => (
                                        <MenuItem value={method.value}>{method.value}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <TextField
                                className={classes.filterField}
                                label={t('reports.startDate')}
                                type="date"
                                value={dateFromFilter}
                                onChange={(e) => setDateFromFilter(e.target.value)}
                                fullWidth
                                variant="outlined"
                                size="small"
                                InputLabelProps={{
                                    shrink: true,
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <TextField
                                className={classes.filterField}
                                label={t('reports.endDate')}
                                type="date"
                                value={dateToFilter}
                                onChange={(e) => setDateToFilter(e.target.value)}
                                fullWidth
                                variant="outlined"
                                size="small"
                                InputLabelProps={{
                                    shrink: true,
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Box className={classes.filterActions}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleClearFilters}
                                    disabled={!hasActiveFilters}>
                                    {t('buttons.cancel')}
                                </Button>
                                <Typography variant="body2" style={{ alignSelf: 'center', marginLeft: 16 }}>
                                    {t('general.showing')} {filteredOrders.length} / {props.productionOrders.length}
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                </AccordionDetails>
            </Accordion>

            {/* Orders Table */}
            <TableContainer component={Paper}>
                <Table aria-label="collapsible table">
                    <TableHead>
                        <TableRow>
                            {columns.map((column) => (
                                <TableCell key={column.id}>{t(column.label)}</TableCell>
                            ))}
                            <TableCell />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginatedOrders.map((order) => (
                            <OrderRow
                                key={order.orderNumber}
                                row={order}
                                markOrderAsDelivered={props.markOrderAsDelivered}
                                userProfile={props.userProfile}
                                history={props.history}
                            />
                        ))}
                    </TableBody>
                </Table>
                <TablePagination
                    rowsPerPageOptions={[25, 50, 100, 200]}
                    component="div"
                    count={filteredOrders.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelRowsPerPage={t('general.rowsPerPage')}
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
                />
            </TableContainer>
        </div>
    );
}
